import { differenceInDays } from "date-fns";
import { ConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { ImageDisplayImageProps } from "src/pages/configurator/components";

import {
  CommonFragment_FloorPlanAssetFragment,
  ConfiguratorContext_CheckoutOptionGroupFragment,
} from "src/fragments/circularFragments.generated";
import {
  SummaryPrintConfiguration_CheckoutConfigFragment,
  SummaryPrintConfiguration_ProductOfferingFragment,
} from "src/pages/configuratorPrint/SummaryPrint.page.generated";
import { Palette } from "~generated/css";
import {
  CheckoutConfigOptionGroupStep,
  Maybe,
  ReadyPlanAssetType,
  ReadyPlanOptionGroupPersonalizationType,
} from "~generated/graphqlTypes";
import { DateOnly, isDefined, stripNumbers } from "~utils";
import {
  ConfiguratorContext_CheckoutConfigFragment,
  ConfiguratorContext_ProductOfferingFragment,
} from "./Configurator.context.generated";
import { AduStep_SelectedPlanFragment } from "./stepTemplates/Adu.step.generated";
import { PlanStep_ProductOfferingFragment } from "./stepTemplates/Plan.step.generated";

export type DisabledStepReason = {
  disabledReason: string;
};

// Return disabled reasons based on the plan option types relevant to current step.
// Does not care about conflicts or parent options. Those are handled in toggleOption()
export function getStepDisabledReasons(contextState: ConfiguratorContext): DisabledStepReason[] {
  const { currentStep } = contextState;

  const stepDisabledReasons =
    currentStep?.getDisabledReasons?.(contextState)?.map((reason) => ({
      disabledReason: reason,
    })) ?? [];

  return [...stepDisabledReasons];
}

/**
 * Utility function to determine if price loading state should be shown for a specific option.
 *
 * The autoSaving and price loading state are coupled because we only want to show loading
 * indicators when we're actually auto-saving AND the price loading state logic determines it should show.
 *
 * Example:
 * - If I select Modern Farmhouse elevation, none of the other elevation options need to flash
 * - If I select Red Brick Scheme, none of the other exterior options need to flash
 * - However, if I select a different elevation, that can change the cost of the other exterior schemes so we need to show the loader
 *
 * Order-based logic:
 * - If an RPO is selected, none of the RPOs in RPOGs with rpog.order < selectedRpo.group.order need to flash
 * - I.e. changing an exterior scheme should not flash any elevations, b/c elevation comes before exterior scheme
 */
export function shouldShowPriceLoading(
  autoSaving: boolean,
  allOptionGroups: ConfiguratorContext_CheckoutOptionGroupFragment[] | undefined,
  optionId: string | undefined,
  lastChangedGroupId: string | null,
): boolean {
  // Only show loading if we're actually auto-saving and have a valid optionId
  if (!autoSaving || !optionId) return false;

  const currentGroup = allOptionGroups?.find((g) => g.options.some((o) => o.option.id === optionId));
  // Don't show loading for options in the same group as the last changed option
  if (!currentGroup || currentGroup.group.id === lastChangedGroupId) return false;

  // Don't show loading for add-on options or personalization options
  const isAddOnOrPersonalization =
    currentGroup.step === CheckoutConfigOptionGroupStep.InteriorPersonalization ||
    currentGroup.step === CheckoutConfigOptionGroupStep.OptionsAddon ||
    currentGroup.step === CheckoutConfigOptionGroupStep.OptionsStructural;

  if (isAddOnOrPersonalization) return false;

  // Find the last changed group for order comparison
  const lastChangedGroup = allOptionGroups?.find((g) => g.group.id === lastChangedGroupId);
  return lastChangedGroup ? currentGroup.group.order >= lastChangedGroup.group.order : false;
}

export function optionGroupFilter(showExpiredOptions: boolean, selectedOptionIds: string[]) {
  return (optionGroup: ConfiguratorContext_CheckoutOptionGroupFragment) => {
    if (!optionGroup.isEnabled) return false;
    if (showExpiredOptions) return true;

    // Don't filter out expired options if they're selected
    const hasSelectedOption = optionGroup.options.some((option) => selectedOptionIds.includes(option.option.id));
    if (hasSelectedOption) return true;
    // Otherwise, only show non-expired options
    return !optionGroup.cutoffDate || getCutoffDaysRemaining(optionGroup.cutoffDate) > 0;
  };
}

// Keep room numbers on Bedroom and Bathrooms
export function maybeStripRoomNumber(rplName: string) {
  const strippedName = stripNumbers(rplName);
  if (numberedRoomNames.includes(strippedName)) {
    return rplName;
  }
  return strippedName;
}

const numberedRoomNames = ["Bedroom", "Bed", "Bathroom", "Bath"];

/**
 * Determine whether the CCOG should render as a listed package selection (PackageOptionCard/PackageOption) or as a swatch selector (ProductAddOnCard/Carousel)
 **/
export function shouldUseSwatchSelector(optionGroup?: ConfiguratorContext_CheckoutOptionGroupFragment): boolean {
  return (
    optionGroup?.group.personalizationType?.code !== ReadyPlanOptionGroupPersonalizationType.Package ||
    !!optionGroup?.group.displayPackageOptionsAsSingleProduct
  );
}

export function getCutoffDaysRemaining(cutoffDate: DateOnly) {
  return differenceInDays(cutoffDate, new Date());
}

/**
 * Helper to map floor plan assets to ImageDisplayImageProps
 */
export function mapFloorPlanAssets(
  floorPlanAssets: CommonFragment_FloorPlanAssetFragment[],
  types: ReadyPlanAssetType[],
  isReversed: boolean,
  floorPlanName?: string,
): ImageDisplayImageProps[] {
  return floorPlanAssets
    .filter((fp) => types.includes(fp.type.code))
    .map((fp) => ({
      kind: "floorPlan",
      id: fp.id,
      imageUrl: fp.asset.downloadUrl,
      isReversed,
      // FooBar Floor Plan -> FooBar Floor
      previewCardName: floorPlanName ?? fp.type.name.replace(" Plan", ""),
    }));
}

/**
 * Returns floor plan images for display, prioritizing level floor plans when available.
 */
export function getFloorPlanImages(
  productOffering: Maybe<
    | ConfiguratorContext_ProductOfferingFragment
    | PlanStep_ProductOfferingFragment
    | SummaryPrintConfiguration_ProductOfferingFragment
  >,
  isReversed: boolean = false,
): ImageDisplayImageProps[] {
  if (!productOffering?.floorPlanAssets) return [];

  // IE 1st & second floor in one view.
  const fullFloorPlan = mapFloorPlanAssets(
    productOffering.floorPlanAssets,
    [ReadyPlanAssetType.FloorPlan],
    isReversed,
    "Floor Plan",
  );

  // Separate views for each floor
  const levelFloorPlans = mapFloorPlanAssets(
    productOffering.floorPlanAssets,
    [ReadyPlanAssetType.GroundBasementFloorPlan, ReadyPlanAssetType.FirstFloorPlan, ReadyPlanAssetType.SecondFloorPlan],
    isReversed,
  );

  // Prioritize level floor plans when available, otherwise use full floor plan
  if (levelFloorPlans.nonEmpty) {
    return levelFloorPlans;
  } else if (fullFloorPlan.nonEmpty) {
    return fullFloorPlan;
  }

  return [];
}

/**
 * Returns ADU images for display, including exterior renderings, floor plans, and interior renderings.
 */
export function getAduImages(
  checkoutConfig: Maybe<ConfiguratorContext_CheckoutConfigFragment | SummaryPrintConfiguration_CheckoutConfigFragment>,
  aduProductOffering?: Maybe<AduStep_SelectedPlanFragment>,
): ImageDisplayImageProps[] {
  if (!checkoutConfig || !checkoutConfig.aduRenderings) return [];

  const interiorRenderings: ImageDisplayImageProps[] = checkoutConfig.aduRenderings
    .filter(
      (rendering) =>
        rendering.type.code === ReadyPlanAssetType.AduKitchen || rendering.type.code === ReadyPlanAssetType.AduLiving,
    )
    // Kitchen first. When user makes updates our order may change.
    .sort((a, b) => {
      if (a.type.code === ReadyPlanAssetType.AduKitchen) return -1;
      if (b.type.code === ReadyPlanAssetType.AduKitchen) return 1;
      return 0;
    })
    .map((render) => ({
      id: render.asset.id,
      kind: "image",
      imageUrl: render.asset.largePreview,
      previewCardName: render.type.name,
      isRendering: true,
      disclaimer: "Renderings do not represent structural upgrades.",
    }));

  const exteriorRendering: ImageDisplayImageProps[] = checkoutConfig.aduRenderings
    .filter((rendering) => rendering.type.code === ReadyPlanAssetType.AduExterior)
    .map((render) => ({
      id: render.asset.id,
      kind: "image",
      imageUrl: render.asset.largePreview,
      previewCardName: "Exterior",
      isRendering: true,
      disclaimer: "Renderings show default colors; your ADU will match your main home.",
    }));

  // New ADU POFs use standard floor plan asset types on the ADU product offering,
  // legacy ADUs use AduFirstFloorPlan/AduSecondFloorPlan on the main house product offering
  const hasAduPof = !!aduProductOffering?.floorPlanAssets?.nonEmpty;
  const floorPlanAssets = hasAduPof
    ? aduProductOffering!.floorPlanAssets
    : (checkoutConfig.productOffering?.floorPlanAssets ?? []);
  const assetTypes = hasAduPof
    ? [ReadyPlanAssetType.FirstFloorPlan, ReadyPlanAssetType.SecondFloorPlan]
    : [ReadyPlanAssetType.AduFirstFloorPlan, ReadyPlanAssetType.AduSecondFloorPlan];
  const aduFloorplans: ImageDisplayImageProps[] = mapFloorPlanAssets(floorPlanAssets, assetTypes, false);

  return [...exteriorRendering, ...aduFloorplans, ...interiorRenderings];
}

/**
 * Lakeside community ID for demo purposes.
 * TODO: Replace with proper feature flag or community property once lot maps are fully supported.
 */
export const LAKESIDE_COMMUNITY_ID = "p:5577";

/**
 * While we get ready for the investor demo.
 * We could even update this to check the query params if we want.
 * TODO: We have one more hack around lot maps that we need to add support for.
 */
export function isLakesideCommunity(communityId: string | null | undefined): boolean {
  return communityId === LAKESIDE_COMMUNITY_ID;
}

/** Builds images specific to the checkout config for use in ImageDisplay */
export function getCCFGImages(
  checkoutConfig: ConfiguratorContext_CheckoutConfigFragment | undefined,
  insideMapsOnly: boolean = false,
): ImageDisplayImageProps[] {
  const floorPlanImages = getFloorPlanImages(checkoutConfig?.productOffering, checkoutConfig?.isReversed);

  // InsideMaps images for non-Lakeside communities
  const insideMapImages = (checkoutConfig?.interiorSubSteps ?? [])
    // The interior sub steps are driven by the "insideMapsRoom" flag on the RPL
    // however there are additional substeps like "Additional Personalization" that
    // are not driven by an insideMapsRoom RPL that need to be filtered out.
    .filter(({ location: rpl }) => isDefined(rpl?.location))
    .map(({ location: rpl, displayName }) => {
      return {
        kind: "insideMaps" as const,
        roomLocationId: rpl?.location.id,
        previewCardName: displayName,
      };
    });

  const interiorImages = insideMapImages;

  if (insideMapsOnly) {
    return insideMapImages;
  }

  return checkoutConfig
    ? [
        ...(checkoutConfig.rendering
          ? [
              {
                kind: "image" as const,
                isRendering: true,
                id: checkoutConfig.rendering.id,
                imageUrl: checkoutConfig.rendering.largePreview!,
                isReversed: !!checkoutConfig.isReversed,
                previewCardName: "Exterior",
              },
            ]
          : []),
        ...interiorImages,
        ...floorPlanImages,
      ]
    : [];
}

export const ADDL_PERSONALIZATION_IMAGE_DISPLAY: ImageDisplayImageProps[] = [
  {
    id: "additional-personalization-image",
    kind: "image",
    imageUrl: "/personalization-placeholder.svg",
    bgColor: Palette.LightBlue,
  },
];
